import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { CardStack } from "@/components/ui/card-stack";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Layers, LayoutList } from "lucide-react";
import { CollectionItem } from "@/types";
import { cn } from "@/lib/utils";
import { Badge } from "../ui/badge";
import { useTranslation } from 'react-i18next';

export interface CollectionItemCardGroupProps {
  group: {
    baseNumber: string;
    items: CollectionItem[];
    count: number;
  };
  onClick?: (group: {
    baseNumber: string;
    items: CollectionItem[];
    count: number;
  }) => void;
  className?: string;
  isOwner?: boolean;
  viewMode?: 'grid' | 'list';
}

export const CollectionItemCardGroup: React.FC<CollectionItemCardGroupProps> = ({
  group,
  onClick,
  className,
  isOwner = false,
  viewMode = 'grid'
}) => {
  const { t } = useTranslation(['collection']);
  const { baseNumber, items, count } = group;

  // Helper function for translations with fallback
  const tWithFallback = (key: string, fallback: string) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  // Find the first item with a valid front image
  const displayItem = items.find(item => 
    item.obverseImage && item.obverseImage !== '/placeholder.svg'
  ) || items[0];

  const imageUrl = displayItem.obverse_image_thumbnail ;
  const denomination = displayItem.banknote ? displayItem.banknote.denomination || '' : '';

  const handleClick = () => {
    if (onClick) onClick(group);
  };

  if (viewMode === 'grid') {
    // Generate stack items using the first valid image for all cards
  const stackItems = items.slice(0, 4).map((item, index) => {
    // Check if this specific item's images should be hidden
      const shouldHideImages = !isOwner && displayItem.hide_images;

    return {
      // Create a unique key using both the item ID and the index
      id: `${item.id}-stack-${index}`,
      content: (
        <Card className="w-full h-full shadow-md overflow-hidden">
          <div className="pt-2 pr-1 pl-1 pb-4 border-b sm:pr-3 sm:pl-3">
            <div className="flex justify-between items-start">
                <h4 className="font-bold"><span>{denomination}</span></h4>
              <div className="pt-2 pr-1 flex items-center text-sm">
                <span>{count}</span>
                <Layers className="h-4 w-4 mr-1" />
              </div>
            </div>

            <div className="gap-0.5 sm:gap-1.5 sm:px-0 flex flex-wrap items-center text-sm">
              {baseNumber && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 h-auto leading-tight bg-muted text-muted-foreground border border-gray-300 shrink-0">
                  {baseNumber}
                </Badge>
              )}
                {displayItem.condition && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto leading-tight ml-1">
                    {displayItem.condition}
                </Badge>
              )}
            </div>
          </div>

          <CardContent className="p-0">
            <div className="w-full">
                {shouldHideImages || !imageUrl || imageUrl === '/placeholder.svg' ? (
                <AspectRatio ratio={4 / 3}>
                  <img
                    src={'/placeholder.svg'}
                    className="w-full h-full object-cover"
                  />
                </AspectRatio>
              ) : (
                <AspectRatio ratio={4 / 3}>
                  <img
                      src={imageUrl}
                      alt={`Collection Item ${displayItem.banknote?.extendedPickNumber || ''}`}
                    className="w-full h-full object-cover"
                  />
                </AspectRatio>
              )}
            </div>
          </CardContent>
        </Card>
      ),
    };
  });

  return (
    <div
      className={cn(
        "group cursor-pointer transition-all flex flex-col",
        className
      )}
      onClick={handleClick}
    >
      <CardStack items={stackItems} offset={6} />
      </div>
    );
  }

  // List view mode
  return (
    <div 
      className={cn(
        "relative cursor-pointer w-full",
        "mb-2",
        "transition-all duration-300",
        className
      )}
      onClick={handleClick}
    >
      {/* Background stacked cards */}
      {Array.from({ length: Math.min(3, count - 1) }).map((_, index) => (
        <Card
          key={`stack-${index}`}
          className={cn(
            "absolute w-full overflow-hidden bg-muted",
            "transition-transform duration-300",
            index === 0 && "translate-y-[2px]",
            index === 1 && "translate-y-[4px]",
            index === 2 && "translate-y-[6px]"
          )}
        >
          <div className="flex items-center p-1 ml-1 w-full h-[70px]" />
        </Card>
      ))}

      {/* Main card */}
      <Card className="relative z-10 overflow-hidden bg-card">
        <div className="flex items-center p-1 ml-1 w-full">
          <div className="flex-shrink-0">
            <div className="h-[58px] w-[68px] relative rounded-md overflow-hidden">
              {!isOwner && displayItem.hide_images || !imageUrl || imageUrl === '/placeholder.svg' ? (
                <img
                  src={'/placeholder.svg'}
                  className="w-full h-full object-contain"
                />
              ) : (
                <img
                  src={imageUrl}
                  alt={`Collection Item ${displayItem.banknote?.extendedPickNumber || ''}`}
                  className="w-full h-full object-contain"
                />
              )}
            </div>
          </div>
          <div className="flex-grow ml-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold flex items-center gap-2">
                <span>{denomination}</span>
                {baseNumber && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 h-auto leading-tight bg-muted text-muted-foreground border border-gray-300 shrink-0">
                    {baseNumber}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="mr-2">
                  <Layers className="w-3 h-3 mr-1" />
                  {count}
                </Badge>
              </div>
            </div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              {displayItem.banknote?.year}
              {displayItem.condition && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto leading-tight">
                  {displayItem.condition}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
